import { Dispatch, SetStateAction } from 'react';
import { type State } from '@popperjs/core';
import { Box } from '../../../layout/Box';
import { ARROW_SIZE, ARROW_X_BREAKPOINT, STYLES_MAP, TOOLTIP_MARGIN } from '../constants';
import { ReactComponent as TooltipArrow } from '../resources/tooltipArrow.svg';
import { ReactComponent as TooltipOnboardingArrow } from '../resources/tooltipOnboardingArrow.svg';
import { TooltipProps } from '../types';

const arrowIcon = (variant: TooltipProps['variant']) => {
    if (variant === 'onboarding') {
        return <TooltipOnboardingArrow width={24} fill="var(--background-invert)" />;
    }

    return <TooltipArrow width={15} fill="var(--background-invert)" />;
};

interface ArrowProps {
    variant: TooltipProps['variant'];
    state: State | null;
    popperElement: HTMLElement | null;
    setArrowElement: Dispatch<SetStateAction<HTMLDivElement | null>>;
}

export const Arrow = ({
    variant = 'default',
    state,
    setArrowElement,
    popperElement,
}: ArrowProps) => {
    const placement = state?.placement ?? '';
    const position = placement.startsWith('top') ? 'top' : 'bottom';
    const align = placement.split('-')[1];

    let xCoord = state?.modifiersData.arrow?.x;

    if (variant === 'onboarding' && xCoord != null) {
        const currentPopperWidth =
            (popperElement?.getBoundingClientRect().width ?? 0) - TOOLTIP_MARGIN * 2 - ARROW_SIZE;

        if (xCoord < ARROW_X_BREAKPOINT) {
            xCoord = ARROW_X_BREAKPOINT;
        } else if (xCoord > currentPopperWidth - ARROW_X_BREAKPOINT) {
            xCoord = currentPopperWidth - ARROW_X_BREAKPOINT;
        }
    }

    const baseStyles = STYLES_MAP[variant][position];
    const iconTransforms: string[] = [];

    if ('transform' in baseStyles) {
        iconTransforms.push(baseStyles.transform);
    }
    if (align === 'end') {
        iconTransforms.push('scaleX(-1)');
    }

    const containerStyle: React.CSSProperties = {
        position: 'absolute',
        left: xCoord != null ? `${xCoord}px` : '',
        top: 'top' in baseStyles ? baseStyles.top : undefined,
        bottom: 'bottom' in baseStyles ? baseStyles.bottom : undefined,
        transform: iconTransforms.join(' '),
    };

    return (
        <Box ref={setArrowElement} style={containerStyle}>
            {arrowIcon(variant)}
        </Box>
    );
};
